import { fetchData } from './dataApi';

const manifests = new Map();
const levelRank = new Map([
  ['simple', 0],
  ['medium', 1],
  ['hard', 2],
  ['very_hard', 3],
]);

async function loadManifest(locale) {
  if (!manifests.has(locale)) {
    manifests.set(locale, fetchData(`conversation/${locale}/index.json`)
      .then((manifest) => ({ manifest, dataLocale: locale }))
      .catch(async () => ({
        manifest: await fetchData('conversation/en/index.json'),
        dataLocale: 'en',
      }))
      .catch((error) => {
        manifests.delete(locale);
        throw error;
      }));
  }
  return manifests.get(locale);
}

function findChapter(manifest, conversationId) {
  return (manifest.chapters || []).find((chapter) => chapter.id === conversationId);
}

export async function loadConversationCategories(locale) {
  const { manifest } = await loadManifest(locale);
  return (manifest.chapters || [])
    .map((chapter) => ({
      id: chapter.id,
      chapterId: chapter.levelOrder,
      label: chapter.title || chapter.id,
      title: chapter.title || '',
      scene: chapter.scene || '',
      context: chapter.context || '',
      images: chapter.images || [],
      level: chapter.level,
      levelOrder: chapter.levelOrder,
      path: chapter.path,
    }))
    .sort((left, right) => {
      const levelDifference = (levelRank.get(left.level) ?? 4) - (levelRank.get(right.level) ?? 4);
      return levelDifference || Number(left.levelOrder) - Number(right.levelOrder);
    });
}

export function mergeConversationCategories(targetCategories = [], nativeCategories = []) {
  const nativeById = new Map(nativeCategories.map((chapter) => [chapter.id, chapter]));
  return targetCategories.map((chapter) => {
    const nativeChapter = nativeById.get(chapter.id);
    return {
      ...chapter,
      nativeTitle: nativeChapter?.title || '',
      nativeScene: nativeChapter?.scene || '',
    };
  });
}

export async function loadConversationDocument(locale, conversationId) {
  const localized = await loadManifest(locale);
  let chapter = findChapter(localized.manifest, conversationId);
  if (chapter) {
    try {
      return await fetchData(`conversation/${localized.dataLocale}/${chapter.path}`);
    } catch (error) {
      if (localized.dataLocale === 'en') throw error;
    }
  }

  const source = await loadManifest('en');
  chapter = findChapter(source.manifest, conversationId);
  if (!chapter) throw new Error(`Unknown conversation: ${conversationId}`);
  return fetchData(`conversation/en/${chapter.path}`);
}

export function conversationLineText(line, speakers = new Map()) {
  if (line.type === 'stage_direction') {
    return line.text ? `[${line.text}]` : '';
  }
  if (line.type !== 'dialogue') return line.text || '';

  const text = (line.segments || []).map((segment) => {
    if (segment.type === 'stage_direction') return segment.text ? `[${segment.text}]` : '';
    return segment.type === 'speech' ? segment.text || '' : '';
  }).filter(Boolean).join(' ').trim();
  const speaker = speakers.get(line.speakerId);
  return speaker && text ? `${speaker}: ${text}` : text;
}

export function conversationDocumentToLegacy(chapter) {
  const speakers = new Map(
    (chapter.speakers || []).map((speaker) => [speaker.id, speaker.displayName]),
  );
  const lines = (chapter.lines || []).map((line) => ({
    id: line.id,
    text: conversationLineText(line, speakers),
  })).filter((line) => line.text);
  return {
    chapterId: chapter.levelOrder || 0,
    conversationId: chapter.conversationId || '',
    title: chapter.title || '',
    scene: chapter.scene || '',
    context: chapter.context || '',
    content: Object.fromEntries(lines.map((line, index) => [String(index + 1), line.text])),
    lineIds: lines.map((line) => line.id),
    images: chapter.images || [],
  };
}

export async function loadConversationLegacyChapter(locale, conversationId) {
  return conversationDocumentToLegacy(await loadConversationDocument(locale, conversationId));
}

export function clearConversationV2Cache() {
  manifests.clear();
}
